import { RouteName, navPages, portfolioPages } from '@/constants/RouteName';
import type { IItemUnderAnalysis } from '@/types/IItemUnderAnalysis';
import { type IPortfolioTree, isPortfolioTreeSubportfolio } from '@/types/IPortfolioTree';
import { getAreAllRouteLevelsAuthorized, type RoutePermissionSource } from '@/utils/permission';
import { isTaggedAsLiveEquityBasketFn, isOfficialMasterIndexFn, getRootItemUnderAnalysis } from '@/utils/portfolioTree';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type {
  RouteLocationNormalized,
  RouteLocationResolvedGeneric,
  RouteRecordNormalized,
  RouteRecordRaw,
  Router,
} from 'vue-router';
import type { UserPermission } from '@/constants/UserPermission';

export type AnalyticsRoute =
  | RouteName.PORTFOLIO_CONSTRUCTION
  | RouteName.PRINCIPAL_COMPONENT_ANALYSIS
  | RouteName.FACTOR_DECOMPOSITION
  | RouteName.CONSTITUENT_RISK
  | RouteName.POSITION;

const ANALYTICS_LANDING_PRIORITY = [
  RouteName.PORTFOLIO_CONSTRUCTION,
  RouteName.PRINCIPAL_COMPONENT_ANALYSIS,
  RouteName.FACTOR_DECOMPOSITION,
  RouteName.CONSTITUENT_RISK,
  RouteName.POSITION,
];

/** Registered analytics tools in landing order, including Position. Deprecated siblings are omitted. */
export function getAnalyticsLandingRoutes(
  routes: readonly (RouteRecordRaw | RouteRecordNormalized)[],
): (RouteRecordRaw | RouteRecordNormalized)[] {
  return ANALYTICS_LANDING_PRIORITY.flatMap((name) => {
    const match = routes.find((route) => route.name === name);
    return match ? [match] : [];
  });
}

export const getAnalyticsToolFromRoute = (route: RouteLocationNormalized): RouteName | null => {
  if (route.matched.some((x) => x.name === RouteName.PORTFOLIO_CONSTRUCTION)) {
    return RouteName.PORTFOLIO_CONSTRUCTION;
  }

  if (route.matched.some((x) => x.name === RouteName.PRINCIPAL_COMPONENT_ANALYSIS)) {
    return RouteName.PRINCIPAL_COMPONENT_ANALYSIS;
  }

  if (route.matched.some((x) => x.name === RouteName.FACTOR_DECOMPOSITION)) {
    return RouteName.FACTOR_DECOMPOSITION;
  }

  if (route.matched.some((x) => x.name === RouteName.CONSTITUENT_RISK)) {
    return RouteName.CONSTITUENT_RISK;
  }

  if (route.matched.some((x) => x.name === RouteName.POSITION)) {
    return RouteName.POSITION;
  }

  return null;
};

/**
 * Maps parent analytics routes to their default child routes.
 * This ensures direct navigation to a specific page rather than triggering redirects.
 * Doing so would allow the user to see the full path they are being directed to when they hover on the Analytics tab.
 *
 * Currently we only need this for portfolio construction and factor decomposition because we no long use substeps
 * as params there and are using child routes for navigating through the substeps.
 */
export const getDefaultChildRoute = (route: RouteName): RouteName => {
  switch (route) {
    case RouteName.PORTFOLIO_CONSTRUCTION:
      return RouteName.PC_PERFORMANCE;
    case RouteName.FACTOR_DECOMPOSITION:
      return RouteName.FD_REGRESSION_TABLE;
    default:
      return route as RouteName;
  }
};

export const getAnalyticsChildrenFromRouter = (router: Router): (RouteRecordRaw | RouteRecordNormalized)[] =>
  router.getRoutes().find((route) => route.name === RouteName.ANALYTICS)?.children ?? [];

/** First registered route the user may open, in `routes` order. */
export const getFirstAuthorizedRoute = ({
  permission,
  routes,
  shouldUseAdminBackDoor,
}: {
  permission: Set<UserPermission>;
  routes: readonly (RouteRecordRaw | RouteRecordNormalized)[];
  shouldUseAdminBackDoor?: boolean;
}): RouteName | undefined => {
  for (const child of routes) {
    if (child.name == null) {
      continue;
    }

    if (
      !getAreAllRouteLevelsAuthorized({
        permission,
        routeLevels: [child] as RoutePermissionSource[],
        shouldUseAdminBackDoor,
      })
    ) {
      continue;
    }

    return child.name as RouteName;
  }
};

/**
 * Because the behavior of the itemUnderAnalysis differs for master indices and live equity baskets based on a given
 * route, this function (and NOT the global state variable, `shouldTreatRootItemAsPortfolio`) should be used when
 * determining which version of a page (portfolio or strategy) we should route to.
 */
export const getShouldUseDraftOfRootItemOnRouteFn = ({
  datum,
  portfolioTreeUnderAnalysis,
  route,
  debug = false,
}: {
  datum: IItemUnderAnalysis | null;
  portfolioTreeUnderAnalysis?: IPortfolioTree | null;
  route: RouteLocationNormalized | RouteLocationResolvedGeneric;
  debug?: boolean;
}): boolean | null => {
  const datumToUse = getRootItemUnderAnalysis(datum, portfolioTreeUnderAnalysis);
  if (debug) console.log('datumToUse', datumToUse);

  if (!datumToUse) return null;

  if (isTaggedAsLiveEquityBasketFn(datumToUse)) {
    if (debug)
      console.log(
        'isTaggedAsLiveEquityBasketFn',
        route.matched.some((x) => x.name === RouteName.PORTFOLIO_CONSTRUCTION),
      );

    return route.matched.some((x) => x.name === RouteName.PORTFOLIO_CONSTRUCTION);
  }

  if (isOfficialMasterIndexFn(datumToUse)) {
    const excludedRoutes = new Set([
      RouteName.PERFORMANCE,
      RouteName.STRATEGY_PERFORMANCE_CONTRIBUTION_PDF,
      RouteName.FACTSHEET,
      RouteName.FACTSHEET_PDF,
    ]);
    const result = !route.matched.some((x) => excludedRoutes.has(x.name as RouteName));
    if (debug) console.log('isOfficialMasterIndexFn', result);

    return result;
  }

  if (debug) console.log('isPortfolioTreeSubportfolio', isPortfolioTreeSubportfolio(datumToUse));

  return isPortfolioTreeSubportfolio(datumToUse)
    ? // classic portfolio case
      !datumToUse.apiConfig?.shouldExposePositions || !datumToUse.apiConfig?.containsOnlyEquities
    : // otherwise return false
      false;
};

export default function (route: RouteLocationNormalized): {
  isOnFactsheetPage: Ref<boolean>;
  isOnPortfolioConstruction: Ref<boolean>;
  isOnFactorComparison: Ref<boolean>;
  isOnFactorDecomposition: Ref<boolean>;
  isOnFactorDecompositionRolling: Ref<boolean>;
  isOnFactorDecompositionRegressionTable: Ref<boolean>;
  isOnPca: Ref<boolean>;
  isOnPortfolioContributions: Ref<boolean>;
  isOnStrategyPerformanceContribution: Ref<boolean>;
  isOnStrategyPerformanceReconciliation: Ref<boolean>;
  isOnCorrelation: Ref<boolean>;
  /**
   * @deprecated - please use `shouldTreatRootItemAsPortfolio` or `getShouldUseDraftOfRootItemOnRouteFn` as appropriate
   * This variable is only used in `useHandleAnalysisStepChanges.ts` which is used in the navigation guard and does not have
   * access to shouldTreatRootItemAsPortfolio. New code should avoid using this variable.
   */
  isPortfolioPage: Ref<boolean>;
  isOnPerformance: Ref<boolean>;
  isOnEfficientFrontier: Ref<boolean>;
  isOnConstituentRisk: Ref<boolean>;
  isNavPages: Ref<boolean>;
  isOnPortfolioConstructionReconciliation: Ref<boolean>;
  isOnCombinedModulePDF: Ref<boolean>;
  isOnStrategyCombinedModulePDF: Ref<boolean>;
  isOnPosition: Ref<boolean>;
  isOnPortfolioConstructionComposition: Ref<boolean>;
  isOnDataPage: Ref<boolean>;
  isOnResourcesPage: Ref<boolean>;
  isOnAIReportPage: Ref<boolean>;
} {
  const isOnPortfolioConstruction = computed(() =>
    route.matched.some((x) => x.name === RouteName.PORTFOLIO_CONSTRUCTION),
  );

  const isOnFactsheetPage = computed(
    () => route.name === RouteName.FACTSHEET || route.name === RouteName.FACTSHEET_PDF,
  );

  const isPortfolioPage = computed(() => route.name !== undefined && portfolioPages.has(route.name as RouteName));

  const isNavPages = computed(() => route.name !== undefined && navPages.has(route.name as RouteName));

  const isOnFactorDecomposition = computed(() =>
    route.matched.some(
      (x) => x.name === RouteName.FACTOR_DECOMPOSITION || x.name === RouteName.FACTOR_DECOMPOSITION_PDF,
    ),
  );

  const isOnFactorDecompositionRolling = computed(() => route.name === RouteName.FD_ROLLING);

  const isOnFactorComparison = computed(() => route.name === RouteName.FD_FACTOR_COMPARISON);

  const isOnFactorDecompositionRegressionTable = computed(() => route.name === RouteName.FD_REGRESSION_TABLE);

  const isOnPca = computed(() => route.name === RouteName.PRINCIPAL_COMPONENT_ANALYSIS);

  const isOnPerformance = computed(() => route.name === RouteName.PC_PERFORMANCE);

  const isOnEfficientFrontier = computed(() => route.name === RouteName.PC_EFFICIENT_FRONTIER);

  const isOnPortfolioContributions = computed(() => route.name === RouteName.PC_CONTRIBUTIONS);

  const isOnCorrelation = computed(() => route.name === RouteName.PC_CORRELATION);

  const isOnPortfolioConstructionReconciliation = computed(() => route.name === RouteName.PC_RECONCILIATION);

  const isOnStrategyPerformanceContribution = computed(
    () => route.name === RouteName.STRATEGY_PERFORMANCE_CONTRIBUTION,
  );
  const isOnStrategyPerformanceReconciliation = computed(
    () => route.name === RouteName.STRATEGY_PERFORMANCE_RECONCILIATION,
  );

  const isOnConstituentRisk = computed(() =>
    [RouteName.CONSTITUENT_RISK, RouteName.CONSTITUENT_RISK_PDF].includes(route.name as RouteName),
  );

  /**
   * It will return true if the user is on the Combined Module PDF page (portfolio or strategy)
   */
  const isOnCombinedModulePDF = computed(
    () =>
      route.name === RouteName.PORTFOLIO_COMBINED_MODULE_PDF || route.name === RouteName.STRATEGY_COMBINED_MODULE_PDF,
  );

  const isOnStrategyCombinedModulePDF = computed(() => route.name === RouteName.STRATEGY_COMBINED_MODULE_PDF);

  const isOnPosition = computed(
    () =>
      route.name === RouteName.POSITION ||
      route.name === RouteName.POSITION_PDF ||
      route.name === RouteName.PC_COMPOSITION,
  );

  const isOnPortfolioConstructionComposition = computed(() => route.name === RouteName.PC_COMPOSITION);

  const isOnDataPage = computed(() => route.name === RouteName.DATA);

  const isOnResourcesPage = computed(() => route.path.includes(RouteName.RESOURCES.toLowerCase()));

  const isOnAIReportPage = computed(() => route.name === RouteName.AI_REPORT);

  return {
    isOnFactsheetPage,
    isOnPortfolioConstruction,
    isOnFactorComparison,
    isOnFactorDecomposition,
    isOnFactorDecompositionRolling,
    isOnFactorDecompositionRegressionTable,
    isOnPca,
    isOnPortfolioContributions,
    isOnStrategyPerformanceContribution,
    isOnStrategyPerformanceReconciliation,
    isOnCorrelation,
    isPortfolioPage,
    isOnPerformance,
    isOnEfficientFrontier,
    isOnConstituentRisk,
    isNavPages,
    isOnPortfolioConstructionReconciliation,
    isOnCombinedModulePDF,
    isOnStrategyCombinedModulePDF,
    isOnPosition,
    isOnPortfolioConstructionComposition,
    isOnDataPage,
    isOnResourcesPage,
    isOnAIReportPage,
  };
}
